import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Slide,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Info, Analytics as AnalyticsIcon, Edit, Delete, OpenInNew } from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

const STORAGE_PREFIX = "ga_looker_report_url_";

const isValidLookerStudioUrl = (url) => {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === "lookerstudio.google.com" &&
      parsed.pathname.includes("/embed/")
    );
  } catch {
    return false;
  }
};

const GoogleAnalyticsIntegration = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  const storageKey = domainId ? `${STORAGE_PREFIX}${domainId}` : null;

  const [reportUrl, setReportUrl] = useState("");
  const [editDialog, setEditDialog] = useState({ open: false, inputValue: "" });
  const [removeDialog, setRemoveDialog] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  // Load stored URL on mount / domain change
  useEffect(() => {
    if (!storageKey) return;
    const stored = localStorage.getItem(storageKey);
    setReportUrl(stored || "");
  }, [storageKey]);

  const handleOpenEditDialog = useCallback(() => {
    setUrlError("");
    setEditDialog({ open: true, inputValue: reportUrl });
  }, [reportUrl]);

  const handleSaveUrl = useCallback(() => {
    const value = editDialog.inputValue.trim();

    if (!value) {
      setUrlError("Please paste your Looker Studio embed URL.");
      return;
    }
    if (!isValidLookerStudioUrl(value)) {
      setUrlError("This doesn't look like a valid Looker Studio embed URL. It should start with https://lookerstudio.google.com/embed/...");
      return;
    }

    localStorage.setItem(storageKey, value);
    setReportUrl(value);
    setEditDialog({ open: false, inputValue: "" });
    setSnackbar({ open: true, severity: "success", message: "Google Analytics report connected!" });
  }, [editDialog.inputValue, storageKey]);

  const handleRemove = useCallback(() => {
    localStorage.removeItem(storageKey);
    setReportUrl("");
    setRemoveDialog(false);
    setSnackbar({ open: true, severity: "success", message: "Report removed." });
  }, [storageKey]);

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: { xs: 2, md: 3 },
        borderRadius: "16px",
        boxShadow: isDarkMode ? "0px 2px 12px rgba(0,0,0,0.2)" : "0px 2px 12px rgba(0,0,0,0.04)",
        minHeight: "500px",
        width: "100%",
      }}
    >
      {/* Edit / Connect Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, inputValue: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#219297", display: "flex", alignItems: "center", gap: 1.5 }}>
          <AnalyticsIcon sx={{ color: "#219297" }} />
          Connect Google Analytics Report
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDarkMode ? "#a0aec0" : "#666", fontSize: "0.85rem", mb: 2 }}>
            Paste the embed URL of your Looker Studio report connected to your GA4 property.
            In Looker Studio, use <strong>File → Embed report</strong> to get this URL.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            placeholder="https://lookerstudio.google.com/embed/reporting/..."
            value={editDialog.inputValue}
            onChange={(e) => {
              setEditDialog((prev) => ({ ...prev, inputValue: e.target.value }));
              setUrlError("");
            }}
            error={!!urlError}
            helperText={urlError}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ padding: "12px 16px" }}>
          <Button onClick={() => setEditDialog({ open: false, inputValue: "" })}>Cancel</Button>
          <Button
            onClick={handleSaveUrl}
            variant="contained"
            sx={{ backgroundColor: "#219297", color: "#fff" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirm Dialog */}
      <Dialog open={removeDialog} onClose={() => setRemoveDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600, color: "#d32f2f", display: "flex", alignItems: "center", gap: 1.5 }}>
          <Info sx={{ color: "#d32f2f", fontSize: 22 }} />
          Remove Report?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDarkMode ? "#a0aec0" : "#666", fontSize: "0.9rem" }}>
            This will remove the connected Analytics report from this portal. Your Looker Studio report itself won't be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 16px" }}>
          <Button onClick={() => setRemoveDialog(false)}>Cancel</Button>
          <Button onClick={handleRemove} variant="contained" sx={{ backgroundColor: "#d32f2f", color: "#fff" }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ color: "#fff", borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <SoftBox display="flex" alignItems="center" gap={2}>
          <AnalyticsIcon sx={{ color: "#219197", fontSize: 32 }} />
          <SoftTypography variant="h5" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : "dark" }}>
            Google Analytics
          </SoftTypography>
        </SoftBox>

        {reportUrl && (
          <SoftBox display="flex" gap={1}>
            <Tooltip title="Open in Looker Studio">
              <IconButton size="small" onClick={() => window.open(reportUrl.replace("/embed/", "/"), "_blank")}>
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Change report URL">
              <IconButton size="small" onClick={handleOpenEditDialog}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => setRemoveDialog(true)} sx={{ color: "#d32f2f" }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </SoftBox>
        )}
      </SoftBox>

      <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text", opacity: 0.7, mb: 3 }}>
        View your Google Analytics report, connected directly through Looker Studio
      </SoftTypography>

      {!reportUrl ? (
        <SoftBox textAlign="center" py={6}>
          <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "#666", mb: 2 }}>
            No Analytics report connected yet.
          </SoftTypography>
          <Button
            variant="contained"
            onClick={handleOpenEditDialog}
            sx={{ backgroundColor: "#219297", color: "#fff", textTransform: "none", fontWeight: 600 }}
          >
            Connect Google Analytics Report
          </Button>
        </SoftBox>
      ) : (
        <SoftBox
          sx={{
            width: "100%",
            height: "800px",
            borderRadius: "12px",
            overflow: "hidden",
            border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
          }}
        >
          <iframe
            title="Google Analytics Report"
            src={reportUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
          />
        </SoftBox>
      )}
    </SoftBox>
  );
};

export default GoogleAnalyticsIntegration